import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, AlertTriangle, FileText } from "lucide-react";

// Post-submit success panel shown inside the "send a case" portal. Receives
// the freshly-created case payload and a callback to reset the form.
//
// Batch mode (iter 56): if `success._batch` is present, render a stacked
// summary listing every submitted case with its case_number + auto-generated
// purchase_order_number, plus any failed cases with the reason. Single-case
// mode keeps the existing hero look.
export function WorkflowSuccessPanel({ success, onSubmitAnother }) {
    const lastName = success.dentist_name?.split(" ").slice(-1)[0] || "";
    const batch = success._batch;
    const isBatch = batch && batch.total > 1;

    return (
        <div id="send-case-success" data-testid="send-case-success" className="rounded-3xl bg-white text-slate-900 p-10 lg:p-14">
            <div className="flex items-center gap-3 text-emerald-600">
                <CheckCircle2 size={26} />
                <div className="font-mono-tr tracking-[0.22em] uppercase text-[12px]">
                    {isBatch ? `Batch received · ${batch.completed.length} of ${batch.total} cases` : "Case received"}
                </div>
            </div>
            <h3 className="mt-5 font-display font-light text-4xl lg:text-5xl tracking-tight">
                Thank you{lastName ? `, Dr. ${lastName}` : ""}.
            </h3>

            {!isBatch && (
                <>
                    <div className="mt-3 text-slate-600 text-[15.5px] leading-relaxed">
                        Your case is queued in our lab. Tracking number:
                    </div>
                    <div className="mt-3 font-mono-tr text-2xl tracking-[0.18em] text-slate-900" data-testid="success-case-number">
                        {success.case_number}
                    </div>
                    {success.purchase_order_number && (
                        <div className="mt-2 text-[13px] text-slate-600">
                            PO number: <span className="font-mono-tr text-slate-900 font-medium" data-testid="success-po-number">{success.purchase_order_number}</span>
                        </div>
                    )}
                    {success.email && (
                        <div className="mt-2 text-[13.5px] text-slate-600">
                            A confirmation will arrive at <span className="text-slate-900 font-medium">{success.email}</span> within 5 minutes.
                        </div>
                    )}
                </>
            )}

            {isBatch && (
                <div className="mt-6 space-y-4">
                    <div className="text-slate-600 text-[15px] leading-relaxed">
                        Each case below got its own tracking number and PO number. They&apos;ll be processed independently in our lab.
                    </div>
                    <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="batch-success-table">
                        <table className="min-w-full text-[13px]">
                            <thead className="bg-slate-50 text-left text-[10.5px] font-mono-tr tracking-[0.16em] uppercase text-slate-500">
                                <tr>
                                    <th className="px-4 py-2.5">#</th>
                                    <th className="px-4 py-2.5">Case number</th>
                                    <th className="px-4 py-2.5">PO number</th>
                                    <th className="px-4 py-2.5">Patient</th>
                                    <th className="px-4 py-2.5">Product</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {batch.completed.map((c, i) => (
                                    <tr key={`ok-${i}`} className="hover:bg-emerald-50/30" data-testid={`batch-row-${i}`}>
                                        <td className="px-4 py-2.5 text-slate-400 font-mono-tr">{i + 1}</td>
                                        <td className="px-4 py-2.5 font-mono-tr text-slate-900">{c.case_number}</td>
                                        <td className="px-4 py-2.5 font-mono-tr text-cyan-700">{c.purchase_order_number}</td>
                                        <td className="px-4 py-2.5 text-slate-700">{c.patient_id}</td>
                                        <td className="px-4 py-2.5 text-slate-600">{c.product}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {batch.failed && batch.failed.length > 0 && (
                        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="batch-failures">
                            <div className="flex items-center gap-2 text-rose-800 font-semibold text-[13px] mb-2">
                                <AlertTriangle size={14} /> {batch.failed.length} case{batch.failed.length !== 1 ? "s" : ""} could not be submitted
                            </div>
                            <ul className="text-[12.5px] text-rose-800 space-y-1.5">
                                {batch.failed.map((f, i) => (
                                    <li key={`fail-${i}`} className="flex items-start gap-2">
                                        <FileText size={11} className="mt-0.5 shrink-0 text-rose-500" />
                                        <span>
                                            <strong>{f.patient_id}</strong> · {f.product} — {f.error}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-2 text-[11.5px] text-rose-700/85">
                                Click <em>Submit another case</em> to re-enter and retry these.
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={onSubmitAnother} className="tr-btn-ghost" data-testid="submit-another-case">
                    {isBatch ? "Submit more cases" : "Submit another case"}
                </button>
                <Link to="/" className="tr-btn">Back to home <ArrowRight size={16} /></Link>
            </div>
        </div>
    );
}
